<template>
  <!--
    SX-01 (doc 22 §7, H-A1; DESIGN-19 P2). V2's three Profile stat tiles,
    fed by the SAME Reports summary endpoint Reports V2 reads
    (`GET /api/ai-tutor/reports/summary`, CAT-1A) and presented under the
    DESIGN-18 data-honesty rules (doc 00 §23.3.1): a genuine 0 renders as ۰,
    `partial` renders its lower bound + «دست‌کم», and `unsupported` /
    `not_observable` / a failed or loading read render the unavailable state
    («—» + a short note). Nothing here is ever fabricated, and a summary that
    cannot be read never blocks the Profile itself.
  -->
  <section
    class="student-profile-stats"
    data-testid="student-profile-stats"
    :aria-label="$t('profile.student.stats.ariaLabel')"
    :aria-busy="loading ? 'true' : 'false'"
  >
    <article
      v-for="tile in tiles"
      :key="tile.key"
      class="student-profile-stats__tile"
      :class="'student-profile-stats__tile--' + tile.status"
      :data-testid="'student-profile-stat-' + tile.key"
      :data-status="tile.status"
      :aria-disabled="tile.hasNumber ? null : 'true'"
    >
      <strong class="student-profile-stats__value">{{ tile.value }}</strong>
      <span class="student-profile-stats__label"
        >{{ tile.label
        }}<template v-if="tile.note">
          {{ ' '
          }}<small class="student-profile-stats__note">{{
            tile.note
          }}</small></template
        ></span
      >
    </article>
  </section>
</template>

<script>
import { collaborators as aiTutorCollaborators } from '@/store/modules/aiTutor'
import { formatReportCount } from '@/utils/reportsFormat'

// V2 order: «کارت یادگیری» / «جلسه معلم» / «مبحث یادگرفته‌شده». The metric
// keys are CAT-1A's (doc 07 §12.2); `learnedTopicCount` is `unsupported`
// today (H-9) and therefore always renders the unavailable state.
const TILES = [
  { key: 'conversations', metric: 'conversationCount' },
  { key: 'teacherSessions', metric: 'teacherSessionCount' },
  { key: 'learnedTopics', metric: 'learnedTopicCount' },
]

// Seam for tests (same convention as the aiTutor store's own collaborators).
export const collaborators = {
  getReportsSummary: () => aiTutorCollaborators.api.getReportsSummary(),
}

export default {
  name: 'StudentProfileStats',
  data() {
    return {
      loading: true,
      failed: false,
      summary: null,
    }
  },
  computed: {
    tiles() {
      const metrics = this.summary && this.summary.metrics
      return TILES.map(tile => {
        const base = {
          key: tile.key,
          label: this.$t(`profile.student.stats.${tile.key}`),
          hasNumber: false,
        }
        if (this.loading) {
          return {
            ...base,
            status: 'loading',
            value: this.$t('aiTutorSetup.reports.tiles.noValue'),
            note: this.$t('aiTutorSetup.reports.tiles.loading'),
          }
        }
        const metric = metrics && metrics[tile.metric]
        if (!metric) {
          return {
            ...base,
            status: 'unavailable',
            value: this.$t('aiTutorSetup.reports.tiles.noValue'),
            note: this.$t('aiTutorSetup.reports.tiles.unavailable'),
          }
        }
        const numeric = typeof metric.value === 'number'
        if (metric.status === 'available' && numeric) {
          return {
            ...base,
            status: 'available',
            hasNumber: true,
            value: this.formatCount(metric.value),
            note: '',
          }
        }
        if (metric.status === 'partial' && numeric) {
          return {
            ...base,
            status: 'partial',
            hasNumber: true,
            value: this.formatCount(metric.value),
            note: this.$t('aiTutorSetup.reports.tiles.partial'),
          }
        }
        if (metric.status === 'not_observable') {
          return {
            ...base,
            status: 'not_observable',
            value: this.$t('aiTutorSetup.reports.tiles.noValue'),
            note: this.$t('aiTutorSetup.reports.tiles.notObservable'),
          }
        }
        return {
          ...base,
          status: 'unsupported',
          value: this.$t('aiTutorSetup.reports.tiles.noValue'),
          note: this.$t('aiTutorSetup.reports.tiles.unsupported'),
        }
      })
    },
  },
  created() {
    this.load()
  },
  methods: {
    formatCount(value) {
      return formatReportCount(value, this.$i18n && this.$i18n.locale)
    },
    async load() {
      this.loading = true
      this.failed = false
      try {
        this.summary = await collaborators.getReportsSummary()
      } catch (e) {
        // Truthful failure: every tile shows the unavailable state; the
        // Profile around it is unaffected.
        this.summary = null
        this.failed = true
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
// Measured from the rendered V2 `.profile-stats` (1440 / 390): a 3-column
// grid whose 1px gaps on the border colour draw the hairline dividers.
.student-profile-stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1px;
  margin: 14px 0 22px;
  overflow: hidden;
  border: 1px solid $borna-border;
  border-radius: 18px;
  background: $borna-border;
}

.student-profile-stats__tile {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  min-width: 0;
  padding: 15px;
  background: $borna-surface;
  text-align: center;
}

.student-profile-stats__value {
  font-size: 18px;
  font-weight: $borna-fw-bold;
  line-height: 1.75;
  color: $borna-text;
}

.student-profile-stats__label {
  display: block;
  font-size: 11px;
  line-height: 1.75;
  color: $borna-muted;
}

// DESIGN-18 unavailable presentation: the value slot keeps its place but is
// muted, and a short note joins the label -- inline on desktop so the tile
// keeps V2's height, on its own line in the mobile shell (doc 00 §23.3.1).
.student-profile-stats__note {
  display: inline;
  margin-inline-start: 4px;
  font-size: 10px;
  line-height: 1.6;
  color: $borna-muted-light;
}

@include borna-mobile {
  .student-profile-stats__note {
    display: block;
    margin-inline-start: 0;
  }
}

.student-profile-stats__tile--loading,
.student-profile-stats__tile--unavailable,
.student-profile-stats__tile--unsupported,
.student-profile-stats__tile--not_observable {
  .student-profile-stats__value {
    color: $borna-muted-light;
  }
}
</style>
